import { useCallback, useState } from "react";
import { Bookmark, ChevronDown, ChevronRight, Clock, FileText, Star } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  spaceNavRowClassName,
  spaceNavRowSelectedClassName,
} from "@/components/library/libraryNavStyles";
import type { ResolvedQuickAccessDoc } from "@/hooks/useLibraryQuickAccess";
import type { LibraryDocRef } from "@/lib/libraryQuickAccess";

function getStoredOpen(key: string, defaultOpen = true): boolean {
  if (typeof window === "undefined") return defaultOpen;
  const stored = localStorage.getItem(key);
  return stored !== null ? stored === "true" : defaultOpen;
}

function persistOpen(key: string, open: boolean) {
  if (typeof window === "undefined") return;
  localStorage.setItem(key, String(open));
}

type QuickAccessSectionProps = {
  title: string;
  icon: React.ReactNode;
  storageKey: string;
  docs: ResolvedQuickAccessDoc[];
  isActive: boolean;
  onSectionClick: () => void;
  onDocumentClick: (ref: LibraryDocRef) => void;
  onUnfavorite?: (ref: LibraryDocRef) => void;
};

function QuickAccessSection({
  title,
  icon,
  storageKey,
  docs,
  isActive,
  onSectionClick,
  onDocumentClick,
  onUnfavorite,
}: QuickAccessSectionProps) {
  const [open, setOpen] = useState(() => getStoredOpen(storageKey));

  const toggleOpen = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const next = !open;
      setOpen(next);
      persistOpen(storageKey, next);
    },
    [open, storageKey]
  );

  return (
    <Collapsible open={open} onOpenChange={(v) => { setOpen(v); persistOpen(storageKey, v); }}>
      <div
        className={cn(
          spaceNavRowClassName,
          "mb-0.5",
          isActive && spaceNavRowSelectedClassName
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0 shrink-0"
          onClick={toggleOpen}
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </Button>
        <button
          type="button"
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
          onClick={onSectionClick}
        >
          {icon}
          <span className="text-sm font-medium truncate">{title}</span>
          {docs.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] shrink-0">
              {docs.length}
            </Badge>
          )}
        </button>
      </div>
      <CollapsibleContent>
        <div className="ml-4 mt-0.5 mb-2 space-y-0.5">
          {docs.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {title === "Favorites"
                ? "No favorites yet"
                : "No recent documents"}
            </p>
          ) : (
            docs.map((doc) => (
              <div
                key={`${doc.itemType}-${doc.id}`}
                className="group flex items-center gap-1 rounded-md hover:bg-accent/50"
              >
                <button
                  type="button"
                  className="flex items-center gap-2 flex-1 min-w-0 px-2 py-1 text-left"
                  onClick={() => onDocumentClick(doc)}
                >
                  <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-xs truncate">{doc.title}</span>
                </button>
                {onUnfavorite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnfavorite(doc);
                    }}
                    title="Remove from favorites"
                  >
                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export type LibrarySidebarQuickAccessProps = {
  activeView: "favorites" | "recent" | null;
  favoriteDocs: ResolvedQuickAccessDoc[];
  recentDocs: ResolvedQuickAccessDoc[];
  onNavigateFavorites: () => void;
  onNavigateRecent: () => void;
  onDocumentClick: (ref: LibraryDocRef, view: "favorites" | "recent") => void;
  onUnfavorite?: (ref: LibraryDocRef) => void;
};

export function LibrarySidebarQuickAccess({
  activeView,
  favoriteDocs,
  recentDocs,
  onNavigateFavorites,
  onNavigateRecent,
  onDocumentClick,
  onUnfavorite,
}: LibrarySidebarQuickAccessProps) {
  return (
    <div className="space-y-1 mb-2">
      <QuickAccessSection
        title="Favorites"
        icon={<Bookmark className="h-4 w-4 text-amber-500 shrink-0" />}
        storageKey="library-sidebar-favorites-open"
        docs={favoriteDocs}
        isActive={activeView === "favorites"}
        onSectionClick={onNavigateFavorites}
        onDocumentClick={(ref) => onDocumentClick(ref, "favorites")}
        onUnfavorite={onUnfavorite}
      />
      <QuickAccessSection
        title="Recent"
        icon={<Clock className="h-4 w-4 text-muted-foreground shrink-0" />}
        storageKey="library-sidebar-recent-open"
        docs={recentDocs}
        isActive={activeView === "recent"}
        onSectionClick={onNavigateRecent}
        onDocumentClick={(ref) => onDocumentClick(ref, "recent")}
      />
    </div>
  );
}
